/**
 * GeometryProcessor - A utility class for processing and transforming Three.js geometries.
 * Provides methods for extracting vertex data, transforming points and objects, and validating geometric data for DXF export.
 */
import * as THREE from 'three';

class GeometryProcessor {
  constructor() {
    this.tolerance = 1e-6; // Tolerance for floating point comparisons
  }

  /**
   * Extract polyline data from a Three.js object
   * @param {THREE.Object3D} threeObject - Three.js object containing polyline geometry
   * @returns {Object|null} Extracted polyline data or null if invalid
   */
  extractPolylineData(threeObject) {
    if (!threeObject) {
      return null;
    }

    try {
      const geometry = threeObject.geometry;
      let points = [];

      // Handle different geometry types
      if (geometry && geometry.isBufferGeometry) {
        points = this._extractFromBufferGeometry(geometry);
      } else if (geometry && geometry.vertices) {
        // Legacy geometry with vertices array
        points = geometry.vertices.map(vertex => ({
          x: vertex.x,
          y: vertex.y,
          z: vertex.z || 0
        }));
      } else if (threeObject.userData && threeObject.userData.points) {
        // Points stored in userData
        points = threeObject.userData.points.map(point => ({
          x: point.x,
          y: point.y,
          z: point.z || 0
        }));
      }

      if (points.length === 0) {
        return null;
      }

      // Apply world matrix transformation if needed
      if (threeObject.matrixWorld) {
        points = this.transformPoints(points, threeObject.matrixWorld);
      }

      return {
        id: threeObject.uuid || this._generateId(),
        points: points,
        closed: this._isClosedPolyline(points),
        segmentCount: Math.max(0, points.length - 1),
        layerName: threeObject.userData?.layerName || 'default',
        metadata: {
          originalObject: threeObject,
          geometryType: geometry ? geometry.type : 'unknown',
          hasValidGeometry: true
        }
      };
    } catch (error) {
      console.error('Error extracting polyline data:', error);
      return null;
    }
  }

  /**
   * Get transformation data from a Three.js object
   * @param {THREE.Object3D} threeObject - Three.js object
   * @returns {Object} Transformation data including position, rotation, and matrix
   */
  getTransformation(threeObject) {
    threeObject.updateWorldMatrix(true, false);
    const position = new THREE.Vector3();
    threeObject.getWorldPosition(position);
    const rotation = new THREE.Euler().setFromQuaternion(threeObject.quaternion, 'XYZ');
    return { position, rotationZ: rotation.z, matrix: threeObject.matrixWorld };
  }

  /**
   * Transform a single point using a transformation matrix
   * @param {Object} point - Point with x, y, z coordinates
   * @param {THREE.Matrix4} matrix - Transformation matrix
   * @returns {Object} Transformed point
   */
  transformPoint(point, matrix) {
    const vector = new THREE.Vector3(point.x, point.y, point.z || 0);
    vector.applyMatrix4(matrix);
    return { x: vector.x, y: vector.y, z: vector.z };
  }

  /**
   * Filter and validate an array of points
   * @param {Array} points - Array of points to validate
   * @returns {Array} Array of valid points
   */
  filterValidPoints(points) {
    return points.filter(p => this._isValidPoint(p));
  }

  /**
   * Transform points using a transformation matrix
   * @param {Array} points - Array of points to transform
   * @param {THREE.Matrix4} matrix - Transformation matrix
   * @returns {Array} Array of transformed points
   */
  transformPoints(points, matrix) {
    return points.map(point => {
      const vector = new THREE.Vector3(point.x, point.y, point.z || 0);
      vector.applyMatrix4(matrix);
      return {
        x: vector.x,
        y: vector.y,
        z: vector.z
      };
    });
  }

  /**
   * Calculate the major axis endpoint for an ellipse, relative to its center.
   * @param {number} radiusX - The major radius of the ellipse.
   * @param {number} rotation - The rotation of the ellipse in radians.
   * @returns {{x: number, y: number, z: number}} The calculated endpoint vector.
   */
  calculateMajorAxisEndpoint(radiusX, rotation) {
    const x = radiusX * Math.cos(rotation);
    const y = radiusX * Math.sin(rotation);
    return { x, y, z: 0 };
  }


  // Private helper methods

  /**
   * Extract points from BufferGeometry
   * @private
   */
  _extractFromBufferGeometry(geometry) {
    const points = [];
    const position = geometry.attributes.position;

    if (position) {
      for (let i = 0; i < position.count; i++) {
        points.push({
          x: position.getX(i),
          y: position.getY(i),
          z: position.getZ ? position.getZ(i) : 0
        });
      }
    }

    return points;
  }

  /**
   * Check if a polyline is closed
   * @private
   */
  _isClosedPolyline(points) {
    if (points.length < 3) return false;
    const first = points[0];
    const last = points[points.length - 1];
    return this._calculateDistance(first, last) < this.tolerance;
  }

  /**
   * Check if a point has valid coordinates
   * @private
   */
  _isValidPoint(point) {
    return point &&
      typeof point.x === 'number' && !isNaN(point.x) &&
      typeof point.y === 'number' && !isNaN(point.y) &&
      (point.z === undefined || (typeof point.z === 'number' && !isNaN(point.z)));
  }

  /**
   * Calculate distance between two points
   * @private
   */
  _calculateDistance(p1, p2) {
    if (!this._isValidPoint(p1) || !this._isValidPoint(p2)) {
      return Infinity;
    }

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dz = (p2.z || 0) - (p1.z || 0);
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * Generate a unique ID for polylines without UUIDs
   * @private
   */
  _generateId() {
    return 'polyline_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  }
}

export default GeometryProcessor;